/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import BetManager from '../Playbar/BetManager';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import Decorators from '../Utility/Decorators';
import PaylineData, { IPaylineData } from './PaylineData';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class PaylinesDefinitionComponent extends ReferencedComponent
{
    // #region Reference Settings
    public InstanceId: string = 'Payline.Definition';
    protected _isActive: boolean = true;

    // #endregion

    /** Payline information. */
    protected paylines: PaylineData[] = null;

    private _paylineCount: number = 0;
    public get PaylineCount(): number { return this._paylineCount; }

    /** Paylines map by id. */
    private _paylineMap: Map<number, PaylineData> = null;
    private _offsetMap: Map<string, number[]> = null;
    private _activeOffsetMap: Map<string, number[]> = null;

    @property({ type: cc.Integer })
    protected offsetCount: number = 3;

    @property({ type: cc.Integer })
    public get GeneratePaylines(): number { return 0; }
    public set GeneratePaylines(value: number)
    {
        this._generateAmount = value;
        this.generatePaylineDefs(value);
    }
    private _generateAmount: number = 0;

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();

    /** @hidden */
    protected setContexts(): void
    {
        this._betManagerRef.SetContext(BetManager);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.paylines = this.getComponentsInChildren(PaylineData).sort((a, b) => a.PaylineID - b.PaylineID - 1);
        this._paylineCount = this.paylines.length;

        this._paylineMap = new Map();
        this._offsetMap = new Map();

        this.paylines.forEach(payline =>
            {
                this._paylineMap.set(payline.PaylineID, payline);

                payline.Offsets.forEach((offset, index) =>
                    {
                        let key: string = `${index}${offset}`;
                        let offsets: number[] = this._offsetMap.get(key);
                        if (!offsets)
                        {
                            offsets = [payline.PaylineID];
                            this._offsetMap.set(key, offsets);
                            return;
                        }

                        offsets.push(payline.PaylineID);
                    });
            });
    }

    protected start(): void
    {
        super.start();

        this._betManagerRef.WithValue(betManager =>
            {
                betManager.ActiveLinesObservable.OnChanged(this.linesChangedHandler);
            });
    }

    protected onDestroy(): void
    {
        this._betManagerRef.WithValue(betManager =>
            {
                betManager.ActiveLinesObservable.OffChanged(this.linesChangedHandler);
            });
        super.onDestroy();
    }

    @Decorators.BindThis()
    private linesChangedHandler(): void
    {
        this._activeOffsetMap = new Map();

        let paylines: IPaylineData[] = this.GetPaylines();
        paylines.forEach(payline =>
            {
                payline.Offsets.forEach((offset, index) =>
                    {
                        let key: string = `${index}${offset}`;
                        let offsets: number[] = this._activeOffsetMap.get(key);
                        if (!offsets)
                        {
                            offsets = [payline.PaylineID];
                            this._activeOffsetMap.set(key, offsets);
                            return;
                        }

                        offsets.push(payline.PaylineID);
                    });
            });
    }

    public GetPaylinesForOffset(reelIndex: number, offset: number, activeOnly: boolean = true): number[]
    {
        let key: string = `${reelIndex}${offset}`;
        let offSetLines: number[] = activeOnly
            ? this._activeOffsetMap.get(key)
            : this._offsetMap.get(key);

        if (!offSetLines) { return null; }
        return offSetLines;
    }

    /** Returns payline data for given id. */
    public GetPayline(id: number = null): PaylineData
    {
        return this._paylineMap.get(id) || null;
    }

    /** Returns all payline data. */
    public GetPaylines(activeOnly: boolean = true): PaylineData[]
    {
        if (!activeOnly || !this._betManagerRef.Value)
        {
            return this.paylines;
        }

        let inactiveStart: number = this._betManagerRef.Value.ActiveLinesObservable.Value;
        return this.paylines.filter(payline => payline.PaylineID < inactiveStart);
    }

    /** Generates paylines defs in scene. */
    private generatePaylineDefs(amount: number): void
    {
        if (!CC_EDITOR) { return; }

        // End recursion.
        if (amount < 1)
        {
            this.onLoad();
            return;
        }

        let paylineID: number = this._generateAmount - amount;
        let paylineData: PaylineData = new cc.Node(`PaylineDef_${paylineID}`)
            .addComponent(PaylineData);
        this.node.addChild(paylineData.node);

        for (let i: number = 0; i < this.offsetCount; i++)
        {

            paylineData.Offsets.push(0);
        }
        paylineData.PaylineID = paylineID;
        // Recurse
        this.generatePaylineDefs(--amount);
    }
}
